import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { updateAdminApp, type AdminRegistryApp } from '@/api/console';
import { Callout, Modal, useToast } from '@/components/ui';

interface FlipAppModalProps {
    /** The app pending the enable/disable confirm; `null` closes the modal. */
    app: AdminRegistryApp | null;
    /**
     * EAA-1817: `app.role_link_count` — the server-computed aggregate on
     * `GET /admin/apps` (`ApplicationService.registry_aggregates()`), not a
     * client-side count. The previous version of this modal took a
     * `roleLinkCount` the PARENT derived from a separate `/role-maps` fetch;
     * that derivation is gone now that the registry row carries the real
     * number itself, so the parent forwards `app.role_link_count` directly.
     */
    roleLinkCount: number;
    onClose: () => void;
}

/**
 * EAA-1819/EAA-1817: shared enable/disable confirm modal for `/console/apps`.
 *
 * Enabling an app IS the production approval gate (D11) — it therefore gets
 * the same destructive-style confirm as disabling (`portal-btn-danger`, not
 * the plain primary button the previous version used for "Duyệt & bật"), and
 * a mandatory warning when the app has zero role links, because that app
 * would go live reachable-but-powerless.
 *
 * Disabling states its consequence WITHOUT a user count: portal-api's
 * `accounts` resource has no GET/list endpoint at all (verified against
 * services/portal-api), so there is no way to source "N người dùng sẽ mất
 * tile" truthfully today. EAA-1829 is the ticket that would add such a count;
 * until then this says what happens, not to how many people.
 */
export function FlipAppModal({ app, roleLinkCount, onClose }: FlipAppModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const flipMutation = useMutation({
        mutationFn: (target: AdminRegistryApp) =>
            updateAdminApp(config.apiBase, accessToken as string, target.code, {
                enabled: !target.enabled,
            }),
        onSuccess: (_data, target) => {
            // EAA-1845: `target` is the PRE-mutation app object (see the interface
            // doc above) — `target.enabled === true` means the action just performed
            // was disable ("tắt"), and vice versa. Keep the two toast keys' polarity
            // matched to this when editing either.
            toast.success(
                t(
                    target.enabled
                        ? 'console.apps.flipModal.toastDisabled'
                        : 'console.apps.flipModal.toastEnabled',
                    { name: target.name, code: target.code }
                )
            );
            onClose();
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            // The launcher's enabled-only registry changed too.
            void queryClient.invalidateQueries({ queryKey: ['apps-registry'] });
            // EAA-1819: role-map sync-status's health counters derive from the
            // same registry data — every mutation here invalidates it too.
            void queryClient.invalidateQueries({ queryKey: ['role-map-sync-status'] });
        },
        onError: (_error, target) => {
            toast.error(
                t('console.apps.flipModal.toastError', { name: target.name, code: target.code })
            );
        },
    });

    function handleClose() {
        if (flipMutation.isPending) return;
        onClose();
    }

    const isEnabling = app !== null && !app.enabled;

    return (
        <Modal
            open={app !== null}
            onCancel={handleClose}
            title={
                isEnabling
                    ? t('console.apps.flipModal.enableTitle')
                    : t('console.apps.flipModal.disableTitle')
            }
            footer={
                <>
                    <button
                        type="button"
                        className="portal-btn"
                        onClick={handleClose}
                        disabled={flipMutation.isPending}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn portal-btn-danger"
                        onClick={() => app && flipMutation.mutate(app)}
                        disabled={flipMutation.isPending}
                    >
                        {flipMutation.isPending
                            ? t('console.apps.flipModal.processingLabel')
                            : isEnabling
                              ? t('console.apps.flipModal.enableButton')
                              : t('console.apps.flipModal.disableTitle')}
                    </button>
                </>
            }
        >
            {app && (
                <>
                    <p>
                        {isEnabling
                            ? t('console.apps.flipModal.enableBody', {
                                  name: app.name,
                                  code: app.code,
                              })
                            : t('console.apps.flipModal.disableBody', {
                                  name: app.name,
                                  code: app.code,
                              })}
                    </p>
                    {isEnabling && roleLinkCount === 0 && (
                        // EAA-1817: label copy verbatim from the frame 45 export ("Trước khi
                        // bật:") — was "Chưa có liên kết vai trò", which said the same fact but
                        // not in the design's words.
                        <Callout
                            tone="warning"
                            label={t('console.apps.flipModal.beforeEnableWarningLabel')}
                        >
                            {t('console.apps.flipModal.beforeEnableWarningBody')}
                        </Callout>
                    )}
                </>
            )}
        </Modal>
    );
}
